import { openDB } from "idb";

/**
 * Serpix offline outbox — persists mutations that were attempted while the
 * device was offline. When we come back online we iterate the queue and
 * replay each request in order, deleting entries as they succeed.
 *
 * Everything is intentionally tiny — one store, one index. Keep it that way.
 *
 * Record shape:
 *   {
 *     id: <auto-increment>,
 *     kind: "task.create" | "task.patch" | "task.delete" | "quick_note.create",
 *     url: "/tasks",
 *     method: "POST" | "PATCH" | "DELETE",
 *     body: {...},
 *     temp_id?: "temp_xyz"     // client-side id for optimistic tasks
 *     token: <access token snapshot at enqueue-time>,
 *     created_at: ISO string,
 *     attempts: 0,
 *     last_error?: string
 *   }
 */

const DB_NAME = "serpix";
const DB_VERSION = 1;
const STORE = "outbox";

let _dbPromise = null;
function db() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) {
          const s = d.createObjectStore(STORE, {
            keyPath: "id",
            autoIncrement: true,
          });
          s.createIndex("created_at", "created_at");
        }
      },
    });
  }
  return _dbPromise;
}

export async function enqueue(entry) {
  const d = await db();
  const row = {
    ...entry,
    attempts: 0,
    created_at: new Date().toISOString(),
  };
  const id = await d.add(STORE, row);
  return { ...row, id };
}

export async function list() {
  const d = await db();
  return d.getAll(STORE);
}

export async function count() {
  const d = await db();
  return d.count(STORE);
}

export async function remove(id) {
  const d = await db();
  return d.delete(STORE, id);
}

export async function updateAttempt(id, err) {
  const d = await db();
  const tx = d.transaction(STORE, "readwrite");
  const row = await tx.store.get(id);
  if (row) {
    row.attempts = (row.attempts || 0) + 1;
    row.last_error = err ? String(err).slice(0, 240) : null;
    await tx.store.put(row);
  }
  await tx.done;
}

export async function clear() {
  const d = await db();
  return d.clear(STORE);
}
